import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import { chartColors, chartGrid, chartSurface, chartText } from '../../lib/chartColors';
import { formatEuViewValue, viewLabels } from '../../utils/euFormatters';

const valueFor = (row, metric, view) => {
  if (view === 'cost_of_living') return row.cost_of_living_index;
  if (view === 'affordability') return row.tank_income_share;
  return metric === 'net' ? row.net : metric === 'tax' ? row.tax : row.gross;
};

export default function EuTimeSeriesChart({ items = [], metric = 'gross', view = 'nominal', height = 420 }) {
  const option = useMemo(() => {
    const dates = [...new Set(items.map(item => item.reference_date))].sort();
    const names = { IT: 'Italia', EU: 'Media UE ufficiale' };
    const byCountry = { IT: {}, EU: {} };
    items.forEach((item) => {
      if (byCountry[item.country]) byCountry[item.country][item.reference_date] = valueFor(item, metric, view);
    });
    const series = ['IT','EU'].map((country, index) => ({
      name: names[country], type: 'line', symbol: 'circle', symbolSize: 6, showSymbol: dates.length < 30,
      connectNulls: false, data: dates.map(date => byCountry[country][date] ?? null),
      lineStyle: { width: country === 'IT' ? 3.5 : 2.5, type: country === 'IT' ? 'solid' : 'dashed', color: chartColors[index], cap: 'round' },
      itemStyle: { color: chartColors[index] }, emphasis: { focus: 'series' },
    }));
    return {
      animationDuration: 260,
      aria: { enabled: true, decal: { show: true }, description: `Andamento settimanale di Italia e media UE per ${viewLabels[view]}` },
      grid: { left: 54, right: 16, top: 72, bottom: 56 },
      legend: { top: 4, textStyle: { fontFamily: 'Rubik', color: chartText, fontSize: 11, fontWeight: 500 } },
      tooltip: {
        trigger: 'axis', confine: true, backgroundColor: chartSurface, borderWidth: 0, padding: 12,
        textStyle: { fontFamily: 'Rubik', color: chartText }, extraCssText: 'border-radius:18px;box-shadow:0 2px 8px rgba(35,25,27,.14);',
        formatter: (params) => { const index = params?.[0]?.dataIndex ?? 0; const date = dates[index] ? dayjs(dates[index]).format('DD/MM/YYYY') : ''; return `<b>Settimana del ${date}</b><br>${params.filter(item => item.value !== null && item.value !== undefined).map(item => `${item.marker} ${item.seriesName}: <b>${formatEuViewValue(item.value, view)}</b>`).join('<br>')}`; },
      },
      xAxis: { type: 'category', data: dates, boundaryGap: false, axisLabel: { hideOverlap: true, color: chartText, fontFamily: 'Rubik', fontSize: 10, formatter: value => dayjs(value).format('MM/YY') }, axisLine: { lineStyle: { color: chartGrid } }, axisTick: { show: false } },
      yAxis: { type: 'value', scale: true, axisLabel: { formatter: value => view === 'nominal' ? `${Number(value).toFixed(2)} €` : view === 'affordability' ? `${Number(value).toFixed(1)}%` : Number(value).toFixed(0), color: chartText, fontFamily: 'Rubik', fontSize: 10 }, splitLine: { lineStyle: { color: chartGrid } }, axisLine: { show: false }, axisTick: { show: false } },
      dataZoom: [{ type: 'inside', filterMode: 'none' }, { type: 'slider', height: 18, bottom: 8, borderColor: 'transparent', backgroundColor: chartSurface, fillerColor: `${chartColors[1]}22`, handleStyle: { color: chartColors[1] } }],
      series,
    };
  }, [items, metric, view]);

  return <ReactECharts style={{ height }} option={option} notMerge lazyUpdate />;
}
